/**
 * ConsoleSessionsPage.jsx · v2.7.63+E48.2
 * ─────────────────────────────────────────────────────────────────────
 * Floor-supervisor overview of every operator currently plugged into a
 * `?console=<map|chat|helpdesk|email>` window. Auto-refreshes every 15s.
 *
 * Rows are aggregated from `admin_console_sessions` via
 * `GET /api/admin/console-sessions`. Rows older than 10 min are pruned
 * server-side; rows 60-180s old show amber (STALE), older than 180s
 * show red (OFFLINE — usually a closed tab that hasn't ticked yet).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, ArrowLeftCircle, BellRing, RefreshCw, ShieldCheck, User2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const REFRESH_MS = 15_000;

const CONSOLE_META = {
  map:      { text: "LIVE MAP", tone: "bg-amber-500/25   border-amber-400   text-amber-100"   },
  chat:     { text: "CHAT",     tone: "bg-emerald-500/25 border-emerald-400 text-emerald-100" },
  helpdesk: { text: "HELPDESK", tone: "bg-sky-500/25     border-sky-400     text-sky-100"     },
  email:    { text: "EMAILS",   tone: "bg-purple-500/25  border-purple-400  text-purple-100"  },
};

const HEARTBEAT_META = {
  green: { label: "LIVE",    tone: "text-emerald-300", dot: "bg-emerald-400 shadow-emerald-400/60 animate-pulse" },
  amber: { label: "STALE",   tone: "text-amber-300",   dot: "bg-amber-400   shadow-amber-400/60"                 },
  red:   { label: "OFFLINE", tone: "text-red-400",     dot: "bg-red-500     shadow-red-500/60"                   },
};

function _formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

function _formatStale(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return "—";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${(seconds % 60).toString().padStart(2, "0")}s ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

// v2.7.63+E48.3 · seconds elapsed since an ISO timestamp (nudge chips)
function _secondsSince(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 1000));
}

export default function ConsoleSessionsPage() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, operators: 0, by_heartbeat: { green: 0, amber: 0, red: 0 }, by_console: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [filterConsole, setFilterConsole] = useState("all");
  const [filterHeartbeat, setFilterHeartbeat] = useState("all");
  // v2.7.63+E48.3 · nudge dialog state
  const [nudgeTarget, setNudgeTarget] = useState(null);   // row being nudged
  const [nudgeMessage, setNudgeMessage] = useState("");
  const [nudgeSending, setNudgeSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/console-sessions");
      setRows(Array.isArray(data?.items) ? data.items : []);
      setStats(data?.stats || { total: 0, operators: 0, by_heartbeat: { green: 0, amber: 0, red: 0 }, by_console: {} });
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load console sessions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterConsole !== "all" && r.console_id !== filterConsole) return false;
      if (filterHeartbeat !== "all" && r.heartbeat !== filterHeartbeat) return false;
      return true;
    });
  }, [rows, filterConsole, filterHeartbeat]);

  // v2.7.63+E48.3 · push a targeted check-in alert (toast + chime) to one
  // console window via the fleet WS bus (heartbeat fallback if WS down).
  const sendNudge = useCallback(async () => {
    if (!nudgeTarget) return;
    setNudgeSending(true);
    try {
      const { data } = await adminApi.post(
        `/admin/console-sessions/${encodeURIComponent(nudgeTarget.id)}/nudge`,
        { message: nudgeMessage.trim() }
      );
      toast.success(`Nudge sent to ${nudgeTarget.operator_name || nudgeTarget.operator_id}`, {
        description: data?.delivered_ws
          ? "Delivered live via WebSocket"
          : "Console WS offline — will deliver on next heartbeat (≤30s)",
      });
      setNudgeTarget(null);
      setNudgeMessage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send nudge");
    } finally {
      setNudgeSending(false);
    }
  }, [nudgeTarget, nudgeMessage, load]);

  const consoleFilterOptions = useMemo(
    () => [
      { key: "all",      label: "All Consoles" },
      { key: "map",      label: "Live Map"     },
      { key: "chat",     label: "Chat"         },
      { key: "helpdesk", label: "Helpdesk"     },
      { key: "email",    label: "Emails"       },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6 space-y-6" data-testid="console-sessions-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <Link to="/admin" className="text-zinc-400 hover:text-amber-300 transition" data-testid="console-sessions-back">
                <ArrowLeftCircle className="h-6 w-6" />
              </Link>
              <h1 className="text-2xl font-black tracking-tight text-amber-300 flex items-center gap-2">
                <Activity className="h-6 w-6" />
                Console Sessions
              </h1>
            </div>
            <p className="text-sm text-zinc-400 mt-1 pl-9">
              Every operator plugged into a <code className="text-amber-300">?console=*</code> window · auto-refresh 15s
            </p>
          </div>
          <button
            onClick={load}
            className="px-3 py-2 rounded-md border border-zinc-700 hover:border-amber-500 hover:text-amber-200 text-sm inline-flex items-center gap-2"
            data-testid="console-sessions-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
            {lastRefresh && (
              <span className="text-[10px] text-zinc-500 ml-2">
                {lastRefresh.toLocaleTimeString()}
              </span>
            )}
          </button>
        </div>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile
            label="Active Sessions"  value={stats.total}
            accent="amber" testid="console-stat-total"
          />
          <StatTile
            label="Unique Operators" value={stats.operators}
            accent="sky" testid="console-stat-operators"
          />
          <StatTile
            label="LIVE"    value={stats.by_heartbeat?.green || 0}
            accent="emerald" testid="console-stat-green"
          />
          <StatTile
            label="STALE"   value={stats.by_heartbeat?.amber || 0}
            accent="yellow" testid="console-stat-amber"
          />
          <StatTile
            label="OFFLINE" value={stats.by_heartbeat?.red || 0}
            accent="red" testid="console-stat-red"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2" data-testid="console-filter-console-group">
            <span className="text-xs uppercase tracking-widest text-zinc-500">Console</span>
            {consoleFilterOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFilterConsole(opt.key)}
                data-testid={`console-filter-console-${opt.key}`}
                className={`px-2 py-1 rounded text-xs font-bold border transition ${
                  filterConsole === opt.key
                    ? "bg-amber-500/20 border-amber-400 text-amber-100"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2" data-testid="console-filter-heartbeat-group">
            <span className="text-xs uppercase tracking-widest text-zinc-500">Heartbeat</span>
            {["all", "green", "amber", "red"].map((k) => (
              <button
                key={k}
                onClick={() => setFilterHeartbeat(k)}
                data-testid={`console-filter-heartbeat-${k}`}
                className={`px-2 py-1 rounded text-xs font-bold border transition ${
                  filterHeartbeat === k
                    ? "bg-amber-500/20 border-amber-400 text-amber-100"
                    : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
                }`}
              >
                {k === "all" ? "All" : HEARTBEAT_META[k]?.label || k.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-200 px-4 py-3 text-sm" data-testid="console-sessions-error">
            {error}
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border border-zinc-800 overflow-hidden" data-testid="console-sessions-table-wrapper">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Operator</th>
                <th className="px-4 py-3 text-left">Employee ID</th>
                <th className="px-4 py-3 text-left">Console</th>
                <th className="px-4 py-3 text-left">Session Duration</th>
                <th className="px-4 py-3 text-left">Heartbeat</th>
                <th className="px-4 py-3 text-left">Last Ping</th>
                <th className="px-4 py-3 text-left">Client</th>
                <th className="px-4 py-3 text-left">Nudge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500" data-testid="console-sessions-loading">
                  Loading…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-zinc-500" data-testid="console-sessions-empty">
                  {rows.length === 0
                    ? "No active console sessions. When an operator opens any window with ?console=map|chat|helpdesk|email they'll appear here."
                    : "No sessions match the selected filters."}
                </td></tr>
              )}
              {filtered.map((r) => {
                const consoleMeta = CONSOLE_META[r.console_id] || { text: (r.console_id || "?").toUpperCase(), tone: "bg-zinc-700 border-zinc-500 text-zinc-100" };
                const hbMeta = HEARTBEAT_META[r.heartbeat] || HEARTBEAT_META.red;
                return (
                  <tr key={r.id} data-testid={`console-session-row-${r.id}`} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User2 className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                        <div>
                          <div className="font-bold text-amber-100" data-testid={`console-session-name-${r.id}`}>
                            {r.operator_name || r.operator_id || "—"}
                          </div>
                          {r.operator_email && (
                            <div className="text-[11px] text-zinc-500">{r.operator_email}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {r.employee_id
                        ? <span className="font-mono text-xs text-zinc-300" data-testid={`console-session-empid-${r.id}`}>{r.employee_id}</span>
                        : <span className="text-[11px] italic text-red-400/70">NO ID</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded border font-extrabold text-[10px] uppercase tracking-widest ${consoleMeta.tone}`}
                        data-testid={`console-session-console-${r.id}`}
                      >
                        {consoleMeta.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-300" data-testid={`console-session-duration-${r.id}`}>
                      {_formatDuration(r.session_seconds)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2" data-testid={`console-session-heartbeat-${r.id}`} data-heartbeat-status={r.heartbeat}>
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${hbMeta.dot} shadow-[0_0_8px_currentColor]`} />
                        <span className={`text-xs font-bold ${hbMeta.tone}`}>{hbMeta.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400" data-testid={`console-session-last-ping-${r.id}`}>
                      {_formatStale(r.stale_seconds)}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <div className="text-[11px] text-zinc-500 truncate" title={r.user_agent}>
                        {r.user_agent || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <button
                          onClick={() => { setNudgeTarget(r); setNudgeMessage(""); }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-500/50 text-amber-200 text-[11px] font-bold hover:bg-amber-500/20 hover:border-amber-400 transition-colors"
                          data-testid={`console-session-nudge-btn-${r.id}`}
                          title="Push a check-in toast + chime to this console"
                        >
                          <BellRing className="h-3.5 w-3.5" />
                          Nudge
                        </button>
                        {r.pending_nudge && (
                          <span
                            className="text-[10px] font-mono text-amber-300/90"
                            data-testid={`console-session-nudge-pending-${r.id}`}
                            title={`"${r.pending_nudge.message}" · from ${r.pending_nudge.from_name}`}
                          >
                            sent {_formatStale(_secondsSince(r.pending_nudge.sent_at))} · awaiting ack
                          </span>
                        )}
                        {!r.pending_nudge && r.last_nudge_ack && (
                          <span
                            className="text-[10px] font-mono text-emerald-300/90"
                            data-testid={`console-session-nudge-acked-${r.id}`}
                          >
                            ✓ acked {_formatStale(_secondsSince(r.last_nudge_ack.at))}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 flex-wrap text-xs text-zinc-500" data-testid="console-sessions-legend">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <span>Sessions auto-prune after 10 min of no heartbeat.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_6px_currentColor]" />
            <span>LIVE ≤ 60s</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_6px_currentColor]" />
            <span>STALE ≤ 180s</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_currentColor]" />
            <span>OFFLINE &gt; 180s</span>
          </div>
        </div>
      </div>

      {/* v2.7.63+E48.3 · Nudge dialog — optional custom message per nudge */}
      <Dialog open={!!nudgeTarget} onOpenChange={(open) => { if (!open) setNudgeTarget(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-700 text-zinc-100 sm:max-w-md" data-testid="console-nudge-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-300 flex items-center gap-2">
              <BellRing className="h-5 w-5" />
              Nudge {nudgeTarget?.operator_name || nudgeTarget?.operator_id || "operator"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Pushes a toast + soft chime to their{" "}
              <span className="text-amber-300 font-bold uppercase">
                {CONSOLE_META[nudgeTarget?.console_id]?.text || nudgeTarget?.console_id}
              </span>{" "}
              window. They tap “I’m here” to acknowledge — their row snaps back to green.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Textarea
              value={nudgeMessage}
              onChange={(e) => setNudgeMessage(e.target.value.slice(0, 280))}
              placeholder='Optional message — default: "Supervisor check-in — please confirm you&apos;re active."'
              className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 min-h-[80px]"
              maxLength={280}
              data-testid="console-nudge-message-input"
            />
            <div className="text-[10px] text-zinc-500 text-right">{nudgeMessage.length}/280</div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setNudgeTarget(null)}
              className="px-3 py-2 rounded-md border border-zinc-700 text-zinc-300 text-sm hover:border-zinc-500 transition-colors"
              data-testid="console-nudge-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={sendNudge}
              disabled={nudgeSending}
              className="px-3 py-2 rounded-md bg-amber-500/20 border border-amber-400 text-amber-100 text-sm font-bold hover:bg-amber-500/40 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="console-nudge-send-btn"
            >
              <BellRing className="h-4 w-4" />
              {nudgeSending ? "Sending…" : "Send Nudge"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  const tones = {
    amber:   "border-amber-500/40   bg-amber-500/10   text-amber-100",
    sky:     "border-sky-500/40     bg-sky-500/10     text-sky-100",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
    yellow:  "border-yellow-500/40  bg-yellow-500/10  text-yellow-100",
    red:     "border-red-500/40     bg-red-500/10     text-red-100",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[accent] || tones.amber}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-3xl font-black mt-1">{value}</div>
    </div>
  );
}
